/**
 * DeskbayLogger - Sistema di logging centralizzato e configurabile
 *
 * Funzionamento:
 * - I logger group vengono caricati dal DB (tabella logger_groups_config)
 * - Se un gruppo è disattivato: NESSUN log in console NÉ in DB
 * - Se un gruppo è attivo: log in console + (opzionale) persistenza DB
 * - Configurazione cached in memoria + localStorage per evitare query continue
 * - Admin panel può aggiornare la config → invalidate cache
 */

import { supabase } from './supabase/client';

// Window.Capacitor è già dichiarato globalmente da @capacitor/core (tipo CapacitorInstance).
// Non lo ridichiariamo qui per evitare il conflitto di declaration merging (TS2717).

// ─────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogGroupName =
    | 'error'
    | 'auth'
    | 'api'
    | 'ai'
    | 'notifications'
    | 'stripe'
    | 'capacitor'
    | 'performance'
    | 'onboarding'
    | 'translations'
    | 'booking'
    | 'security';

export interface LoggerGroupConfig {
    group_name: LogGroupName;
    display_name: string;
    description?: string;
    icon: string;
    color: string;
    is_active: boolean;
    log_to_db: boolean;
    log_to_console: boolean;
    min_level: LogLevel;
    sort_order: number;
}

export interface LogEntry {
    group: LogGroupName;
    level: LogLevel;
    message: string;
    context?: string;
    metadata?: Record<string, unknown>;
    stack?: string;
}

// ─────────────────────────────────────────────────────────────────
// LEVEL PRIORITY (for min_level filtering)
// ─────────────────────────────────────────────────────────────────

const LEVEL_PRIORITY: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
};

function isLevelAllowed(entryLevel: LogLevel, minLevel: LogLevel): boolean {
    return LEVEL_PRIORITY[entryLevel] >= LEVEL_PRIORITY[minLevel];
}

// ─────────────────────────────────────────────────────────────────
// CACHE
// ─────────────────────────────────────────────────────────────────

const CACHE_KEY = 'deskbay_logger_config';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minuti

let memoryCache: LoggerGroupConfig[] | null = null;
let cacheTimestamp = 0;

function getCachedConfig(): LoggerGroupConfig[] | null {
    if (memoryCache && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
        return memoryCache;
    }
    // Prova localStorage come fallback
    try {
        const stored = localStorage.getItem(CACHE_KEY);
        if (stored) {
            const { data, timestamp } = JSON.parse(stored);
            if (Date.now() - timestamp < CACHE_TTL_MS) {
                memoryCache = data;
                cacheTimestamp = timestamp;
                return data;
            }
        }
    } catch {
        // ignore
    }
    return null;
}

function setCachedConfig(config: LoggerGroupConfig[]) {
    memoryCache = config;
    cacheTimestamp = Date.now();
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: config, timestamp: cacheTimestamp }));
    } catch {
        // ignore
    }
}

export function invalidateLoggerCache() {
    memoryCache = null;
    cacheTimestamp = 0;
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch {
        // ignore
    }
}

// ─────────────────────────────────────────────────────────────────
// LOAD CONFIG FROM SUPABASE — NON-BLOCKING
// ─────────────────────────────────────────────────────────────────

// Flag to avoid concurrent background fetches
let isFetchingConfig = false;

/**
 * Returns config SYNCHRONOUSLY from cache (or defaults).
 * Triggers a background fetch to refresh cache if stale — never blocks the caller.
 */
function getConfig(): LoggerGroupConfig[] {
    const cached = getCachedConfig();
    if (cached) return cached;

    // No cache: return defaults immediately, fetch in background
    const defaults = getDefaultConfig(); // also populates cache
    if (!isFetchingConfig) {
        isFetchingConfig = true;
        void Promise.resolve(supabase
            .from('logger_groups_config')
            .select('group_name, display_name, description, icon, color, is_active, log_to_db, log_to_console, min_level, sort_order')
            .order('sort_order', { ascending: true })
            .then(({ data, error }) => {
                if (!error && data && data.length > 0) {
                    setCachedConfig(data as LoggerGroupConfig[]);
                }
            })
        ).catch(() => { /* silent — defaults already in use */ })
          .finally(() => { isFetchingConfig = false; });
    }
    return defaults;
}

function getDefaultConfig(): LoggerGroupConfig[] {
    const defaults: LoggerGroupConfig[] = [
        { group_name: 'error', display_name: 'Errori', icon: 'alert-triangle', color: '#ef4444', is_active: true, log_to_db: true, log_to_console: true, min_level: 'error', sort_order: 0, description: '' },
        { group_name: 'auth', display_name: 'Auth', icon: 'shield', color: '#8b5cf6', is_active: true, log_to_db: true, log_to_console: true, min_level: 'info', sort_order: 1, description: '' },
        { group_name: 'api', display_name: 'API & Database', icon: 'database', color: '#06b6d4', is_active: true, log_to_db: true, log_to_console: true, min_level: 'info', sort_order: 2, description: '' },
        { group_name: 'ai', display_name: 'AI & Gemini', icon: 'brain', color: '#ec4899', is_active: true, log_to_db: true, log_to_console: true, min_level: 'info', sort_order: 3, description: '' },
        { group_name: 'notifications', display_name: 'Notifiche', icon: 'bell', color: '#f59e0b', is_active: true, log_to_db: true, log_to_console: true, min_level: 'info', sort_order: 4, description: '' },
        { group_name: 'stripe', display_name: 'Stripe', icon: 'credit-card', color: '#10b981', is_active: false, log_to_db: true, log_to_console: true, min_level: 'info', sort_order: 5, description: '' },
        { group_name: 'capacitor', display_name: 'Capacitor', icon: 'smartphone', color: '#3b82f6', is_active: false, log_to_db: true, log_to_console: true, min_level: 'debug', sort_order: 6, description: '' },
        { group_name: 'performance', display_name: 'Performance', icon: 'zap', color: '#f97316', is_active: false, log_to_db: false, log_to_console: true, min_level: 'warn', sort_order: 7, description: '' },
        { group_name: 'onboarding', display_name: 'Onboarding', icon: 'user-check', color: '#6366f1', is_active: false, log_to_db: true, log_to_console: false, min_level: 'info', sort_order: 8, description: '' },
        { group_name: 'translations', display_name: 'Traduzioni', icon: 'languages', color: '#84cc16', is_active: false, log_to_db: true, log_to_console: false, min_level: 'warn', sort_order: 9, description: '' },
        { group_name: 'booking', display_name: 'Prenotazioni', icon: 'calendar', color: '#14b8a6', is_active: false, log_to_db: false, log_to_console: false, min_level: 'info', sort_order: 10, description: '' },
        { group_name: 'security', display_name: 'Sicurezza', icon: 'lock', color: '#dc2626', is_active: true, log_to_db: true, log_to_console: true, min_level: 'warn', sort_order: 11, description: '' },
    ];
    setCachedConfig(defaults);
    return defaults;
}

// ─────────────────────────────────────────────────────────────────
// CORE LOG FUNCTION
// ─────────────────────────────────────────────────────────────────

function log(entry: LogEntry): void {
    const config = getConfig(); // SYNCHRONOUS — never blocks the caller
    const groupConfig = config.find(c => c.group_name === entry.group);

    // Se il gruppo non esiste o è disattivato: SILENZIO ASSOLUTO
    if (!groupConfig || !groupConfig.is_active) return;

    // Controlla il livello minimo configurato
    if (!isLevelAllowed(entry.level, groupConfig.min_level)) return;

    // ── CONSOLE OUTPUT ──────────────────────────────────────────
    if (groupConfig.log_to_console) {
        const prefix = `[${groupConfig.display_name}]${entry.context ? `[${entry.context}]` : ''}`;
        const consoleFn = entry.level === 'error' ? console.error
            : entry.level === 'warn' ? console.warn
                : entry.level === 'debug' ? console.debug
                    : console.log;

        if (entry.metadata && Object.keys(entry.metadata).length > 0) {
            consoleFn(prefix, entry.message, entry.metadata);
        } else {
            consoleFn(prefix, entry.message);
        }
    }

    // ── DB PERSISTENCE — fire-and-forget, never blocks ──────────
    if (groupConfig.log_to_db) {
        persistLogToDB(entry).catch(() => { });
    }
}

async function persistLogToDB(entry: LogEntry): Promise<void> {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;

        // No session → skip DB write to avoid 401 errors before auth is ready
        if (!user?.id) return;

        await supabase.from('app_logs').insert({
            group_name: entry.group,
            level: entry.level,
            message: entry.message,
            context: entry.context || null,
            user_id: user?.id || null,
            metadata: entry.metadata || {},
            stack_trace: entry.stack || null,
            page_url: typeof window !== 'undefined' ? window.location.href : null,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
            platform: detectPlatform(),
        });
    } catch {
        // Silent fail
    }
}

function detectPlatform(): 'web' | 'ios' | 'android' {
    if (typeof window === 'undefined') return 'web';
    // Capacitor globals detection
    const cap = window.Capacitor;
    if (cap?.getPlatform) {
        const p = cap.getPlatform();
        if (p === 'ios') return 'ios';
        if (p === 'android') return 'android';
    }
    return 'web';
}

// ─────────────────────────────────────────────────────────────────
// PUBLIC API — Grouped Loggers
// ─────────────────────────────────────────────────────────────────

type LogFn = (message: string, context?: string, metadata?: Record<string, unknown>) => void;
type ErrorLogFn = (message: string, error?: Error | unknown, context?: string, metadata?: Record<string, unknown>) => void;

function makeGroupLogger(group: LogGroupName) {
    const logFn = (level: LogLevel): LogFn => (message, context, metadata) => {
        log({ group, level, message, context, metadata });
    };

    const errorFn = (level: LogLevel): ErrorLogFn => (message, error, context, metadata) => {
        const stack = error instanceof Error ? error.stack : undefined;
        const meta = {
            ...metadata,
            ...(error instanceof Error ? { errorMessage: error.message } : { error }),
        };
        log({ group, level, message, context, metadata: meta, stack });
    };

    return {
        debug: logFn('debug'),
        info: logFn('info'),
        warn: logFn('warn'),
        error: errorFn('error'),
    };
}

// ─────────────────────────────────────────────────────────────────
// EXPORTED LOGGER GROUPS
// ─────────────────────────────────────────────────────────────────

export const logger = {
    /** Errori critici e eccezioni non gestite */
    error: makeGroupLogger('error'),
    /** Autenticazione e sessioni */
    auth: makeGroupLogger('auth'),
    /** API e Database Supabase */
    api: makeGroupLogger('api'),
    /** Operazioni AI / Gemini */
    ai: makeGroupLogger('ai'),
    /** Push notifications e email */
    notifications: makeGroupLogger('notifications'),
    /** Stripe, pagamenti, webhook */
    stripe: makeGroupLogger('stripe'),
    /** Capacitor mobile bridge */
    capacitor: makeGroupLogger('capacitor'),
    /** Performance e velocità */
    performance: makeGroupLogger('performance'),
    /** Flusso onboarding */
    onboarding: makeGroupLogger('onboarding'),
    /** Traduzioni i18n */
    translations: makeGroupLogger('translations'),
    /** Prenotazioni */
    booking: makeGroupLogger('booking'),
    /** Sicurezza e violazioni */
    security: makeGroupLogger('security'),

    /** Utility: misura il tempo di esecuzione e logga nel gruppo performance */
    async measure<T>(
        name: string,
        fn: () => Promise<T>,
        context?: string
    ): Promise<T> {
        const start = performance.now();
        try {
            const result = await fn();
            const duration = performance.now() - start;
            log({
                group: 'performance',
                level: duration > 1000 ? 'warn' : 'info',
                message: `${name}: ${duration.toFixed(0)}ms${duration > 1000 ? ' ⚠️ SLOW' : ''}`,
                context,
                metadata: { duration_ms: Math.round(duration) }
            });
            return result;
        } catch (error) {
            const duration = performance.now() - start;
            log({
                group: 'performance',
                level: 'error',
                message: `${name} FAILED after ${duration.toFixed(0)}ms`,
                context,
                stack: error instanceof Error ? error.stack : undefined,
                metadata: { duration_ms: Math.round(duration) }
            });
            throw error;
        }
    },

    /** Invalida la cache della config (chiamato dopo ogni toggle dal panel) */
    invalidateCache: invalidateLoggerCache,

    /** Ritorna la config corrente (utile per debug) */
    getConfig: getConfig,
};

export default logger;
